import React, { useEffect, useState, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Layout from '../components/Layout';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const COLORS = ['#6382e6','#EF9F27','#F0997B','#5DCAA5','#A78BFA','#F472B6'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];

function DateRangePicker({ startDate, endDate, onChange, onClear }) {
  const [open, setOpen]           = useState(false);
  const [viewYear, setViewYear]   = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [hovered, setHovered]     = useState(null);
  const ref = useRef();

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const daysInMonth = (y,m) => new Date(y,m+1,0).getDate();
  const firstDay    = (y,m) => new Date(y,m,1).getDay();
  const toStr = d => d ? d.toISOString().split('T')[0] : null;
  const fromStr = s => s ? new Date(s+'T00:00:00') : null;
  const start = fromStr(startDate);
  const end   = fromStr(endDate);

  const handleDayClick = dateStr => {
    const clicked = new Date(dateStr+'T00:00:00');
    if (!startDate || (startDate && endDate)) { onChange(dateStr, null); }
    else { clicked < start ? onChange(dateStr, startDate) : onChange(startDate, dateStr); }
  };

  const isInRange = dateStr => {
    if (!startDate) return false;
    const d = new Date(dateStr+'T00:00:00');
    const s = start;
    const e = endDate ? end : (hovered ? new Date(hovered+'T00:00:00') : null);
    if (!e) return false;
    return (s<=d&&d<=e)||(e<=d&&d<=s);
  };

  const prevMonth = () => viewMonth===0 ? (setViewMonth(11),setViewYear(y=>y-1)) : setViewMonth(m=>m-1);
  const nextMonth = () => viewMonth===11 ? (setViewMonth(0),setViewYear(y=>y+1)) : setViewMonth(m=>m+1);

  const buildGrid = () => {
    const days=daysInMonth(viewYear,viewMonth), first=firstDay(viewYear,viewMonth), cells=[];
    for(let i=0;i<first;i++) cells.push(null);
    for(let d=1;d<=days;d++) {
      cells.push(`${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`);
    }
    return cells;
  };

  const formatDisplay = () => {
    if (!startDate&&!endDate) return 'Select date range';
    const fmt = s => new Date(s+'T00:00:00').toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'});
    if (startDate&&!endDate) return `${fmt(startDate)} → ...`;
    return `${fmt(startDate)} → ${fmt(endDate)}`;
  };

  const applyPreset = label => {
    const now=new Date(), toS=d=>d.toISOString().split('T')[0], e=toS(now);
    let s;
    if(label==='Today')        s=e;
    if(label==='Last 7 days')  { const d=new Date(now); d.setDate(d.getDate()-6);  s=toS(d); }
    if(label==='Last 30 days') { const d=new Date(now); d.setDate(d.getDate()-29); s=toS(d); }
    if(label==='This month')   s=`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`;
    if(label==='This year')    s=`${now.getFullYear()}-01-01`;
    onChange(s,e); setOpen(false);
  };

  const cells = buildGrid();

  return (
    <div ref={ref} style={{position:'relative',display:'inline-block'}}>
      <button type="button" onClick={()=>setOpen(o=>!o)} style={{
        display:'flex',alignItems:'center',gap:8,padding:'8px 14px',
        border:'1px solid #dde1ec',borderRadius:7,
        background:(startDate||endDate)?'#E6F1FB':'#fff',
        color:(startDate||endDate)?'#0C447C':'#555',
        fontSize:13,cursor:'pointer',fontFamily:'inherit',minWidth:240,justifyContent:'space-between'
      }}>
        <span style={{display:'flex',alignItems:'center',gap:7}}>
          <i className="fa-solid fa-calendar-days" style={{color:'#6382e6'}}></i>
          {formatDisplay()}
        </span>
        {(startDate||endDate)
          ? <span onClick={e=>{e.stopPropagation();onClear();}} style={{color:'#aaa',fontSize:12}}>
              <i className="fa-solid fa-xmark"></i>
            </span>
          : <i className="fa-solid fa-chevron-down" style={{fontSize:11,color:'#aaa'}}></i>
        }
      </button>

      {open && (
        <div style={{position:'absolute',top:'calc(100% + 6px)',left:0,zIndex:999,
          background:'#fff',border:'1px solid #e8e8f0',borderRadius:12,
          boxShadow:'0 8px 32px rgba(0,0,0,0.12)',display:'flex',overflow:'hidden',minWidth:520}}>
          <div style={{width:140,borderRight:'1px solid #f0f0f8',padding:'12px 0',background:'#f8f9ff'}}>
            <div style={{fontSize:11,fontWeight:600,color:'#aaa',padding:'0 14px 8px',textTransform:'uppercase',letterSpacing:'0.06em'}}>Quick select</div>
            {['Today','Last 7 days','Last 30 days','This month','This year'].map(p=>(
              <div key={p} onClick={()=>applyPreset(p)}
                style={{padding:'8px 14px',fontSize:13,cursor:'pointer',color:'#555',transition:'all 0.1s'}}
                onMouseEnter={e=>e.target.style.background='#E6F1FB'}
                onMouseLeave={e=>e.target.style.background='transparent'}>{p}</div>
            ))}
          </div>
          <div style={{padding:16}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
              <button type="button" onClick={prevMonth}
                style={{border:'none',background:'none',cursor:'pointer',padding:'4px 8px',fontSize:16,color:'#555'}}>‹</button>
              <div style={{fontWeight:600,fontSize:14,color:'#1a1a2e'}}>{MONTHS[viewMonth]} {viewYear}</div>
              <button type="button" onClick={nextMonth}
                style={{border:'none',background:'none',cursor:'pointer',padding:'4px 8px',fontSize:16,color:'#555'}}>›</button>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(7,36px)',gap:2,marginBottom:4}}>
              {DAYS.map(d=><div key={d} style={{textAlign:'center',fontSize:11,fontWeight:600,color:'#aaa',padding:'4px 0'}}>{d}</div>)}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(7,36px)',gap:2}}>
              {cells.map((cell,idx)=>{
                if(!cell) return <div key={idx}/>;
                const inRange=isInRange(cell);
                const isS=cell===startDate, isE=cell===endDate;
                const isToday=cell===new Date().toISOString().split('T')[0];
                return (
                  <div key={cell}
                    onClick={()=>handleDayClick(cell)}
                    onMouseEnter={()=>startDate&&!endDate&&setHovered(cell)}
                    onMouseLeave={()=>setHovered(null)}
                    style={{
                      width:36,height:36,display:'flex',alignItems:'center',justifyContent:'center',
                      borderRadius:(isS||isE)?'50%':inRange?0:6,
                      background:(isS||isE)?'#6382e6':inRange?'#E6F1FB':'transparent',
                      color:(isS||isE)?'#fff':inRange?'#0C447C':isToday?'#6382e6':'#333',
                      fontWeight:(isS||isE||isToday)?600:400,fontSize:13,cursor:'pointer',
                      border:isToday&&!isS&&!isE?'1px solid #6382e6':'1px solid transparent',
                      transition:'all 0.1s'
                    }}>
                    {parseInt(cell.split('-')[2])}
                  </div>
                );
              })}
            </div>
            <div style={{marginTop:12,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontSize:12,color:'#aaa'}}>
                {startDate&&endDate
                  ? `${Math.round((new Date(endDate)-new Date(startDate))/(1000*60*60*24)+1)} days selected`
                  : startDate?'Select end date':'Select start date'}
              </span>
              <button type="button" onClick={()=>setOpen(false)} className="btn btn-primary btn-sm">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats]         = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [consultantFilter, setConsultantFilter] = useState('');
  const [clientFilter, setClientFilter]         = useState('');
  const [consultants, setConsultants]           = useState([]);
  const [clients, setClients]                   = useState([]);
  const { user, isSalesMgr, isFullViewer } = useAuth();

  useEffect(() => {
    api.get('/lookups/all').then(({ data }) => {
      setConsultants(data.ats_consultants || []);
      setClients(data.clients || []);
    }).catch(() => {});
  }, []);

  const loadStats = async (sd, ed, consultantId, clientId) => {
    try {
      // Fetch all tickets in pages to avoid hardcoded limit
      let page = 1, allTickets = [], total = null;
      while (total === null || allTickets.length < total) {
        let url = `/tickets?limit=500&page=${page}`;
        if (sd)           url += `&date_from=${sd}`;
        if (ed)           url += `&date_to=${ed}`;
        if (consultantId) url += `&consultant_id=${consultantId}`;
        if (clientId)     url += `&client_id=${clientId}`;
        const { data } = await api.get(url);
        allTickets = [...allTickets, ...data.data];
        total = data.total;
        if (data.data.length < 500) break; // last page
        page++;
      }
      const tickets = allTickets;
      const byStatus={}, byPriority={}, byConsultant={}, byClient={}, bySalesMgr={};
      tickets.forEach(t => {
        byStatus[t.status]       = (byStatus[t.status]||0)+1;
        byPriority[t.priority]   = (byPriority[t.priority]||0)+1;
        if(t.ats_consultant) byConsultant[t.ats_consultant] = (byConsultant[t.ats_consultant]||0)+1;
        if(t.client)         byClient[t.client]             = (byClient[t.client]||0)+1;
        if(t.sales_manager)  bySalesMgr[t.sales_manager]   = (bySalesMgr[t.sales_manager]||0)+1;
      });
      setStats({
        total: tickets.length,
        open:       byStatus['Open']||0,
        inProgress: byStatus['In Progress']||0,
        resolved:   Object.entries(byStatus).filter(([k])=>k.startsWith('Resolved')).reduce((a,[,v])=>a+v,0),
        closed:     byStatus['Closed']||0,
        statusData:     Object.entries(byStatus).map(([name,value])=>({name,value})),
        priorityData:   Object.entries(byPriority).map(([name,value])=>({name,value})),
        consultantData: Object.entries(byConsultant).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value).slice(0,8),
        clientData:     Object.entries(byClient).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value).slice(0,8),
        salesMgrData:   Object.entries(bySalesMgr).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value).slice(0,8),
      });
    } catch(err) {
      console.error('Dashboard load error', err);
    }
  };

  useEffect(()=>{ loadStats('','','',''); },[]);

  const handleDateChange = (sd,ed) => {
    setStartDate(sd||''); setEndDate(ed||'');
    loadStats(sd||'', ed||'', consultantFilter, clientFilter);
  };
  const handleClear = () => {
    setStartDate(''); setEndDate('');
    loadStats('', '', consultantFilter, clientFilter);
  };
  const handleConsultantChange = (val) => {
    setConsultantFilter(val);
    loadStats(startDate, endDate, val, clientFilter);
  };
  const handleClientChange = (val) => {
    setClientFilter(val);
    loadStats(startDate, endDate, consultantFilter, val);
  };
  const handleClearAllFilters = () => {
    setStartDate(''); setEndDate(''); setConsultantFilter(''); setClientFilter('');
    loadStats('','','','');
  };

  if (!stats) return (
    <Layout title="Dashboard">
      <div style={{color:'#aaa',textAlign:'center',padding:60}}>
        <i className="fa-solid fa-spinner fa-spin" style={{fontSize:28}}></i>
      </div>
    </Layout>
  );

  const showOnlyOwn = isSalesMgr;

  return (
    <Layout title="Dashboard">
      {showOnlyOwn && (
        <div style={{background:'#E6F1FB',border:'1px solid #85B7EB',borderRadius:8,
          padding:'10px 16px',marginBottom:16,fontSize:13,color:'#0C447C',
          display:'flex',alignItems:'center',gap:8}}>
          <i className="fa-solid fa-eye"></i>
          Showing tickets assigned to <strong style={{marginLeft:4}}>{user?.full_name}</strong> as Sales Manager
        </div>
      )}

      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20,flexWrap:'wrap'}}>
        <span style={{fontSize:13,fontWeight:500,color:'#555'}}>
          <i className="fa-solid fa-filter" style={{marginRight:6,color:'#6382e6'}}></i>Filters:
        </span>

        <DateRangePicker startDate={startDate} endDate={endDate} onChange={handleDateChange} onClear={handleClear}/>

        {/* ATS Consultant filter — hidden for sales manager (already scoped to own tickets) */}
        {!showOnlyOwn && (
          <select
            value={consultantFilter}
            onChange={e => handleConsultantChange(e.target.value)}
            style={{
              padding:'8px 12px', border:'1px solid #dde1ec', borderRadius:7,
              fontSize:13, background: consultantFilter ? '#E6F1FB' : '#fff',
              color: consultantFilter ? '#0C447C' : '#555', minWidth:170
            }}>
            <option value="">All ATS Consultants</option>
            {consultants.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}

        {/* Client filter */}
        <select
          value={clientFilter}
          onChange={e => handleClientChange(e.target.value)}
          style={{
            padding:'8px 12px', border:'1px solid #dde1ec', borderRadius:7,
            fontSize:13, background: clientFilter ? '#E6F1FB' : '#fff',
            color: clientFilter ? '#0C447C' : '#555', minWidth:170
          }}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        {(startDate||endDate||consultantFilter||clientFilter) && (
          <>
            <button className="btn btn-secondary btn-sm" onClick={handleClearAllFilters}>
              <i className="fa-solid fa-xmark"></i> Clear all
            </button>
            <span style={{fontSize:12,color:'#6382e6',fontWeight:500}}>
              Showing {stats.total} ticket{stats.total!==1?'s':''}
            </span>
          </>
        )}
      </div>

      <div className="stat-cards">
        {[
          {label:'Total Tickets',value:stats.total,      sub:(startDate||consultantFilter||clientFilter)?'filtered':'all time', color:'#6382e6'},
          {label:'Open',         value:stats.open,       sub:'needs attention',               color:'#378ADD'},
          {label:'In Progress',  value:stats.inProgress, sub:'being worked',                  color:'#EF9F27'},
          {label:'Resolved',     value:stats.resolved,   sub:'all resolved types',            color:'#5DCAA5'},
        ].map(s=>(
          <div className="stat-card" key={s.label}>
            <div className="label">{s.label}</div>
            <div className="value" style={{color:s.color}}>{s.value}</div>
            <div className="sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Status pie + Priority bar ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>

        {/* Pie chart — no inline labels, use legend below instead */}
        <div className="chart-wrap">
          <div className="card-title"><i className="fa-solid fa-chart-pie"></i> By Status</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={stats.statusData}
                cx="50%" cy="45%"
                outerRadius={75}
                dataKey="value"
                labelLine={false}
                label={false}>
                {stats.statusData.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
              </Pie>
              <Tooltip formatter={(value, name) => [value, name]}/>
            </PieChart>
          </ResponsiveContainer>
          {/* Custom legend below pie */}
          <div style={{display:'flex',flexWrap:'wrap',gap:'6px 14px',justifyContent:'center',marginTop:4}}>
            {stats.statusData.map((entry,i)=>(
              <div key={entry.name} style={{display:'flex',alignItems:'center',gap:5,fontSize:11}}>
                <div style={{width:10,height:10,borderRadius:'50%',background:COLORS[i%COLORS.length],flexShrink:0}}></div>
                <span style={{color:'#555'}}>{entry.name.replace('Resolved - ','R- ')}</span>
                <span style={{fontWeight:600,color:'#333'}}>{entry.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Priority bar — with value labels */}
        <div className="chart-wrap">
          <div className="card-title"><i className="fa-solid fa-chart-bar"></i> By Priority</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.priorityData} margin={{top:20,right:20,left:-10,bottom:5}}>
              <XAxis dataKey="name" tick={{fontSize:12}}/>
              <YAxis tick={{fontSize:11}}/>
              <Tooltip/>
              <Bar dataKey="value" fill="#6382e6" radius={[4,4,0,0]}
                label={{position:'top', fontSize:12, fontWeight:600, fill:'#333'}}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Consultant + Sales Manager ── */}
      {!showOnlyOwn && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
          <div className="chart-wrap">
            <div className="card-title"><i className="fa-solid fa-user-tie"></i> By ATS Consultant</div>
            <ResponsiveContainer width="100%" height={Math.max(280, stats.consultantData.length * 34)}>
              <BarChart data={stats.consultantData} layout="vertical"
                margin={{top:5, right:50, left:10, bottom:5}}>
                <XAxis type="number" tick={{fontSize:11}} hide/>
                <YAxis type="category" dataKey="name" tick={{fontSize:11}} width={100}/>
                <Tooltip/>
                <Bar dataKey="value" fill="#5DCAA5" radius={[0,4,4,0]}
                  label={{position:'right', fontSize:11, fontWeight:600, fill:'#333'}}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-wrap">
            <div className="card-title"><i className="fa-solid fa-user-tie" style={{color:'#EF9F27'}}></i> By Sales Manager</div>
            <ResponsiveContainer width="100%" height={Math.max(280, stats.salesMgrData.length * 34)}>
              <BarChart data={stats.salesMgrData} layout="vertical"
                margin={{top:5, right:50, left:10, bottom:5}}>
                <XAxis type="number" tick={{fontSize:11}} hide/>
                <YAxis type="category" dataKey="name" tick={{fontSize:11}} width={100}/>
                <Tooltip/>
                <Bar dataKey="value" fill="#A78BFA" radius={[0,4,4,0]}
                  label={{position:'right', fontSize:11, fontWeight:600, fill:'#333'}}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Clients ── */}
      <div className="chart-wrap">
        <div className="card-title"><i className="fa-solid fa-building"></i> By Client (Top 8)</div>
        <ResponsiveContainer width="100%" height={Math.max(280, stats.clientData.length * 36)}>
          <BarChart data={stats.clientData} layout="vertical"
            margin={{top:5, right:55, left:10, bottom:5}}>
            <XAxis type="number" tick={{fontSize:11}} hide/>
            <YAxis type="category" dataKey="name" tick={{fontSize:11}} width={120}/>
            <Tooltip/>
            <Bar dataKey="value" fill="#EF9F27" radius={[0,4,4,0]}
              label={{position:'right', fontSize:11, fontWeight:600, fill:'#333'}}/>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Layout>
  );
}